// src/app/services/hooks/useCollectionOrderService.ts
import useFetch from '../../../hooks/useFetch';
import { ServiceEvents } from '../shared/interfaces';
import { useState } from 'react';
import { EndPoints } from '../EndPoints';
import {
  CollectionOrderFilters,
  CollectionOrderStorePayload,
  CollectionOrderUpdatePayload,
} from '../../models/CollectionOrder';

const useCollectionOrderService = () => {
  const { doGet, doPost, doPut, doDelete } = useFetch();

  // flags existentes
  const [fetchingGetCollectionOrders, setFetchingGetCollectionOrders] = useState(false);
  const [fetchingCreateCollectionOrder, setFetchingCreateCollectionOrder] = useState(false);
  const [fetchingStoreCollectionOrder, setFetchingStoreCollectionOrder] = useState(false);
  const [fetchingEditCollectionOrder, setFetchingEditCollectionOrder] = useState(false);
  const [fetchingUpdateCollectionOrder, setFetchingUpdateCollectionOrder] = useState(false);
  const [fetchingDeleteCollectionOrder, setFetchingDeleteCollectionOrder] = useState(false);
  const [fetchingActiveCollectionOrder, setFetchingActiveCollectionOrder] = useState(false);
  const [fetchingShowCollectionOrder, setFetchingShowCollectionOrder] = useState(false);

  // flags batches
  const [fetchingGetOrderBatches, setFetchingGetOrderBatches] = useState(false);
  const [fetchingCreateOrderBatchOptions, setFetchingCreateOrderBatchOptions] = useState(false);
  const [fetchingStoreOrderBatch, setFetchingStoreOrderBatch] = useState(false);
  const [fetchingStoreOrderBatchByBarcode, setFetchingStoreOrderBatchByBarcode] = useState(false);
  const [fetchingDeleteOrderBatch, setFetchingDeleteOrderBatch] = useState(false);
  const [fetchingUpdateBatchStatus, setFetchingUpdateBatchStatus] = useState(false);

  // flags collectors
  const [fetchingGetOrderCollectors, setFetchingGetOrderCollectors] = useState(false);
  const [fetchingCreateOrderCollectorOptions, setFetchingCreateOrderCollectorOptions] = useState(false);
  const [fetchingStoreOrderCollector, setFetchingStoreOrderCollector] = useState(false);
  const [fetchingDeleteOrderCollector, setFetchingDeleteOrderCollector] = useState(false);

  // flag de historial
  const [fetchingGetOrderHistory, setFetchingGetOrderHistory] = useState(false);

  // flag incidencias
  const [fetchingGetOrderIncidents, setFetchingGetOrderIncidents] = useState(false);
  const [fetchingStoreOrderIncident, setFetchingStoreOrderIncident] = useState(false);
  const [fetchingUpdateOrderIncident, setFetchingUpdateOrderIncident] = useState(false);
  const [fetchingDeleteOrderIncident, setFetchingDeleteOrderIncident] = useState(false);


  // ---------- ORDERS ----------
  const getCollectionOrders = (filters: CollectionOrderFilters, events: ServiceEvents = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        query.set(k, String(v));
      }
    });
    const queryString = query.toString();

    return doGet({
      ...events,
      url: `${EndPoints.COLLECTION_ORDERS.GET_COLLECTION_ORDERS}${queryString ? `?${queryString}` : ''}`,
      setFetching: setFetchingGetCollectionOrders,
    });
  };

  const createCollectionOrder = (events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.COLLECTION_ORDERS.CREATE_COLLECTION_ORDER,
      setFetching: setFetchingCreateCollectionOrder,
    });

  const storeCollectionOrder = (data: CollectionOrderStorePayload, events: ServiceEvents = {}) =>
    doPost({
      ...events,
      url: EndPoints.COLLECTION_ORDERS.STORE_COLLECTION_ORDER,
      body: data,
      setFetching: setFetchingStoreCollectionOrder,
    });

  const editCollectionOrder = (id: number, events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.COLLECTION_ORDERS.EDIT_COLLECTION_ORDER.replace(':id', id.toString()),
      setFetching: setFetchingEditCollectionOrder,
    });

  const updateCollectionOrder = (
    id: number,
    data: CollectionOrderUpdatePayload,
    events: ServiceEvents = {}
  ) =>
    doPut({
      ...events,
      url: EndPoints.COLLECTION_ORDERS.UPDATE_COLLECTION_ORDER.replace(':id', id.toString()),
      body: data,
      setFetching: setFetchingUpdateCollectionOrder,
    });

  const deleteCollectionOrder = (id: number, events: ServiceEvents = {}) =>
    doDelete({
      ...events,
      url: EndPoints.COLLECTION_ORDERS.DELETE_COLLECTION_ORDER.replace(':id', id.toString()),
      setFetching: setFetchingDeleteCollectionOrder,
    });

  const activeCollectionOrder = (id: number, active: boolean, events: ServiceEvents = {}) =>
    doPost({
      ...events,
      url: EndPoints.COLLECTION_ORDERS.ACTIVE_COLLECTION_ORDER.replace(':id', id.toString()),
      body: { active },
      setFetching: setFetchingActiveCollectionOrder,
    });

  // ---------- BATCHES dentro de la orden ----------
  const getOrderBatches = (orderId: number, events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.COLLECTION_ORDER_BATCHES.GET_COLLECTION_ORDER_BATCHES.replace(':id', orderId.toString()),
      setFetching: setFetchingGetOrderBatches,
    });

  const createOrderBatchOptions = (orderId: number, events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.COLLECTION_ORDER_BATCHES.CREATE_COLLECTION_ORDER_BATCH.replace(':id', orderId.toString()),
      setFetching: setFetchingCreateOrderBatchOptions,
    });

  /**
   * body esperado por el backend:
   * { batch_id: number, used_quantity?: number }
   */
  const storeOrderBatch = (
    orderId: number,
    data: { batch_id: number; used_quantity?: number },
    events: ServiceEvents = {}
  ) =>
    doPost({
      ...events,
      url: EndPoints.COLLECTION_ORDER_BATCHES.STORE_COLLECTION_ORDER_BATCH.replace(':id', orderId.toString()),
      body: data,
      setFetching: setFetchingStoreOrderBatch,
    });

  /**
   * Agregar lote a la orden mediante código de barras
   * body esperado por el backend:
   * { barcode: string, used_quantity?: number }
   */
  const storeOrderBatchByBarcode = (
    orderId: number,
    data: { barcode: string; used_quantity?: number },
    events: ServiceEvents = {}
  ) =>
    doPost({
      ...events,
      url: EndPoints.COLLECTION_ORDER_BATCHES.STORE_COLLECTION_ORDER_BATCH_BY_BARCODE.replace(':id', orderId.toString()),
      body: data,
      setFetching: setFetchingStoreOrderBatchByBarcode,
    });

  const deleteOrderBatch = (linkId: number, events: ServiceEvents = {}) =>
    doDelete({
      ...events,
      url: EndPoints.COLLECTION_ORDER_BATCHES.DELETE_COLLECTION_ORDER_BATCH.replace(':link_id', linkId.toString()),
      setFetching: setFetchingDeleteOrderBatch,
    });

  // ---------- COLLECTORS dentro de la orden ----------
  const getOrderCollectors = (orderId: number, events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.COLLECTION_ORDER_COLLECTORS.GET_COLLECTION_ORDER_COLLECTORS.replace(':id', orderId.toString()),
      setFetching: setFetchingGetOrderCollectors,
    });

  const createOrderCollectorOptions = (orderId: number, events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.COLLECTION_ORDER_COLLECTORS.CREATE_COLLECTION_ORDER_COLLECTOR.replace(':id', orderId.toString()),
      setFetching: setFetchingCreateOrderCollectorOptions,
    });

  /**
   * body esperado por el backend:
   * { user_id: number, is_lead?: boolean }
   */
  const storeOrderCollector = (
    orderId: number,
    data: { user_id: number; is_lead?: boolean },
    events: ServiceEvents = {}
  ) =>
    doPost({
      ...events,
      url: EndPoints.COLLECTION_ORDER_COLLECTORS.STORE_COLLECTION_ORDER_COLLECTOR.replace(':id', orderId.toString()),
      body: data,
      setFetching: setFetchingStoreOrderCollector,
    });

  const deleteOrderCollector = (linkId: number, events: ServiceEvents = {}) =>
    doDelete({
      ...events,
      url: EndPoints.COLLECTION_ORDER_COLLECTORS.DELETE_COLLECTION_ORDER_COLLECTOR.replace(':link_id', linkId.toString()),
      setFetching: setFetchingDeleteOrderCollector,
    });

  const showCollectionOrder = (id: number, events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.COLLECTION_ORDERS.SHOW_COLLECTION_ORDER.replace(':id', id.toString()),
      setFetching: setFetchingShowCollectionOrder,
    });

  // HISTORIAL de la orden
  const getCollectionOrderHistory = (
    id: number,
    params: { page?: number; page_size?: number; event_type?: string; date_from?: string; date_to?: string } = {},
    events: ServiceEvents = {}
  ) => {
    const q = new URLSearchParams();
    if (params.page) q.set('page', String(params.page));
    if (params.page_size) q.set('page_size', String(params.page_size));
    if (params.event_type) q.set('event_type', params.event_type);
    if (params.date_from) q.set('date_from', params.date_from);
    if (params.date_to) q.set('date_to', params.date_to);

    const url =
      EndPoints.COLLECTION_ORDERS.HISTORY_COLLECTION_ORDER.replace(':id', id.toString()) +
      (q.toString() ? `?${q.toString()}` : '');

    return doGet({
      ...events,
      url,
      setFetching: setFetchingGetOrderHistory,
    });
  };

  // ---------- INCIDENCIAS de la orden ----------
  const getOrderIncidents = (
    orderId: number,
    params: { page?: number; page_size?: number; status?: string; reason?: string; date_from?: string; date_to?: string; q?: string } = {},
    events: ServiceEvents = {}
  ) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') q.set(k, String(v)); });
    const url =
      EndPoints.COLLECTION_ORDER_INCIDENTS.GET_COLLECTION_ORDER_INCIDENTS.replace(':id', String(orderId)) +
      (q.toString() ? `?${q}` : '');
    return doGet({ ...events, url, setFetching: setFetchingGetOrderIncidents });
  };

  const storeOrderIncident = (
    orderId: number,
    body: FormData | {
      date: string;
      time: string;
      reason_id: number;
      description: string;
      solution?: string;
      status?: 'OPEN' | 'CLOSED';
    },
    events: ServiceEvents = {}
  ) =>
    doPost({
      ...events,
      url: EndPoints.COLLECTION_ORDER_INCIDENTS.STORE_COLLECTION_ORDER_INCIDENT.replace(':id', String(orderId)),
      body,
      setFetching: setFetchingStoreOrderIncident,
    });

  const updateOrderIncident = (
    incidentId: number,
    body: FormData | {
      date?: string;
      time?: string;
      reason_id?: number;
      description?: string;
      solution?: string | null;
      status?: 'OPEN' | 'CLOSED';
      remove_evidence?: boolean;
    },
    events: ServiceEvents = {}
  ) => {
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

    return doPut({
      ...events,
      url: EndPoints.COLLECTION_ORDER_INCIDENTS.UPDATE_COLLECTION_ORDER_INCIDENT
              .replace(':incident_id', String(incidentId)),
      body,
      requestType: isFormData ? 'multipart' : 'json',
      setFetching: setFetchingUpdateOrderIncident,
    });
  };

  // Eliminar (DELETE soft)
  const deleteOrderIncident = (incidentId: number, events: ServiceEvents = {}) =>
    doDelete({
      ...events,
      url: EndPoints.COLLECTION_ORDER_INCIDENTS.DELETE_COLLECTION_ORDER_INCIDENT.replace(':incident_id', String(incidentId)),
      setFetching: setFetchingDeleteOrderIncident,
    });

  const updateBatchStatus = (
  linkId: number,
  status: string,
  events: ServiceEvents = {}
  ) =>
    doPut({
      ...events,
      url: EndPoints.COLLECTION_ORDER_BATCHES.UPDATE_BATCH_STATUS.replace(':link_id', linkId.toString()),
      body: { status },
      requestType: 'json',
      setFetching: setFetchingUpdateBatchStatus,
    });

  return {
    // flags - orders
    fetchingGetCollectionOrders,
    fetchingCreateCollectionOrder,
    fetchingStoreCollectionOrder,
    fetchingEditCollectionOrder,
    fetchingUpdateCollectionOrder,
    fetchingDeleteCollectionOrder,
    fetchingActiveCollectionOrder,
    fetchingShowCollectionOrder,

    // flags - batches
    fetchingGetOrderBatches,
    fetchingCreateOrderBatchOptions,
    fetchingStoreOrderBatch,
    fetchingStoreOrderBatchByBarcode,
    fetchingDeleteOrderBatch,
    fetchingUpdateBatchStatus,

    // flags - collectors
    fetchingGetOrderCollectors,
    fetchingCreateOrderCollectorOptions,
    fetchingStoreOrderCollector,
    fetchingDeleteOrderCollector,

    // flag - history
    fetchingGetOrderHistory,

    // flags - incidents
    fetchingGetOrderIncidents,
    fetchingStoreOrderIncident,
    fetchingUpdateOrderIncident,
    fetchingDeleteOrderIncident,

    // métodos - orders
    getCollectionOrders,
    createCollectionOrder,
    storeCollectionOrder,
    editCollectionOrder,
    updateCollectionOrder,
    deleteCollectionOrder,
    activeCollectionOrder,
    showCollectionOrder,

    // métodos - batches
    getOrderBatches,
    createOrderBatchOptions,
    storeOrderBatch,
    storeOrderBatchByBarcode,
    deleteOrderBatch,
    updateBatchStatus,

    // métodos - collectors
    getOrderCollectors,
    createOrderCollectorOptions,
    storeOrderCollector,
    deleteOrderCollector,

    // método - history
    getCollectionOrderHistory,

    // métodos - incidents
    getOrderIncidents,
    storeOrderIncident,
    updateOrderIncident,
    deleteOrderIncident,
  };
};

export default useCollectionOrderService;